import { iconArrowRight } from "assets/icons";
import { Button } from "components/Button";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

interface Property {
  name?: string;
  value?: string;
  action?: () => void;
}

export interface Location {
  locationId: string;
  eventId: string;
  name: string;
  description: string;
  capacity: number;
  isActive: boolean;
  insertDate: string;
  updateDate: string;
}

interface LocationRowProps {
  name: string;
  description: string;
  capacity: string;
  status: string;
  insertDate: string;
  actions: JSX.Element[];
}

export const useLocationsTable = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const initialTableFilters: FilterProps = {};

  const [properties, setProperties] = useState<Property[]>();
  const [locations, setLocations] = useState<Location[]>();
  const [locationRows, setLocationRows] = useState<LocationRowProps[]>([]);
  const [tableFilters, setTableFilters] =
    useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [locationsPerPage, setLocationsPerPage] = useState<number>(10);
  const [locationsCount, setLocationsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const searchTextFormRef = useRef<any>(null);
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();
  const { setToast } = useToast();
  const { push } = useHistory();

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    };
  }, []);

  useEffect(() => {
    getLocations();
  }, [eventId, pageNumber, locationsPerPage, searchText, tableFilters]);

  const rowActionButton = (locationId: string) => {
    return (
      <Button
        label=""
        typeBtn="default"
        icon={iconArrowRight}
        onClick={() => push(`/events/${eventId}/location/${locationId}/edit`)}
        block={false}
      />
    );
  };

  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const searchTextValue = formData.get("searchText") as string;
    setSearchText(searchTextValue);
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const { name, value } = event.target;
    setTableFilters((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value,
      },
    }));
    setPageNumber(1);
  };

  const handleFilterRemove = (filterName: string) => {
    setTableFilters({
      ...tableFilters,
      [filterName]: {
        ...tableFilters[filterName],
        value: undefined,
      },
    });
  };

  const handleSearchTextRemove = () => {
    setSearchText("");
    if (searchTextFormRef?.current) {
      searchTextFormRef.current.reset();
    }
  };

  const handlePageSelected = (page: number): void => {
    setPageNumber(page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    setLocationsPerPage(Number(event.target.value));
    setPageNumber(1);
  };

  const getLocations = async () => {
    if (!eventId) return;

    setLoading(true);
    setError(false);

    try {
      const query = `
        query BackofficeLocations($filter: LocationFilterInput!, $pagination: PaginationInput) {
          backofficeLocations(filter: $filter, pagination: $pagination) {
            locationId
            eventId
            name
            description
            capacity
            isActive
            insertDate
            updateDate
          }
          backofficeLocationsCount(filter: $filter)
        }
      `;

      const variables = {
        filter: {
          eventId,
          searchText: searchText || undefined,
        },
        pagination: {
          offset: (pageNumber - 1) * locationsPerPage,
          limit: locationsPerPage,
        },
      };

      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: "Falha ao carregar locais",
        useNewApi: true,
      });

      const locationsData = response.data.backofficeLocations || [];
      const totalCount = response.data.backofficeLocationsCount || 0;

      setLocations(locationsData);
      setLocationsCount(totalCount);

      // Build table rows
      const rows: LocationRowProps[] = locationsData.map(
        (location: Location) => ({
          name: location.name,
          description: location.description || "-",
          capacity: location.capacity ? location.capacity.toString() : "-",
          status: location.isActive ? "Ativo" : "Inativo",
          insertDate: new Date(location.insertDate).toLocaleDateString("pt-BR"),
          actions: [rowActionButton(location.locationId)],
        })
      );

      setLocationRows(rows);

      // Build properties for cards
      const props: Property[] = [
        { name: "Total de Locais", value: totalCount.toString() },
      ];

      setProperties(props);
    } catch (error) {
      console.error("Error loading locations:", error);
      setError(true);
      setToast({
        type: "error",
        message: "Erro ao carregar locais",
        isActive: true,
        timeToRemoveToast: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    properties,
    locationRows,
    locationsCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    locationsPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
    locations,
    getLocations,
  };
};
